'use client'

import {
  Box,
  Button,
  Container,
  Field,
  HStack,
  Heading,
  Icon,
  Image,
  Input,
  Link,
  Portal,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Dialog } from '@/components/ui/Dialog'
import type { AxiosError } from 'axios'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa'
import { FaXTwitter } from 'react-icons/fa6'
import { FiMail } from 'react-icons/fi'
import { useApiContext } from '@/utils/api'
import type {
  IRCompanyConfig,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'

interface FooterSectionProps {
  useContextFunction: () => InvestorRelationsConfigContextType
}

const STAFF_ACCESS_QUERY_KEY = 'staff-access'
const ASSIDUOUS_ICON_DATA_URL =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xNi4yMzM1IDIuMzYxMjlDMTUuOTYyOCAxLjg3OTU3IDE1LjI4NjIgMS44Nzk1NyAxNS4wMTU1IDIuMzYxMjlMMC4wOTUyNjAxIDI4LjkxNkMtMC4xNzU0MDIgMjkuMzk3OCAwLjE2MjkyNSAzMCAwLjcwNDI0OCAzMEg4LjA0ODk0QzguMzAwMTcgMzAgOC41MzIzMSAyOS44NjIyIDguNjU3OTMgMjkuNjM4NkwxOS45MDU5IDkuNjE5ODVDMjAuMDMxNCA5LjM5NjI5IDIwLjAzMTQgOS4xMjA4MyAxOS45MDU5IDguODk3MjdMMTYuMjMzNSAyLjM2MTI5Wk0yMS4zNjAxIDEzLjM1ODZMMTkuMDI5NSAxNy4yNzYzTDE5LjE1MjYgMTcuNDYxMUwyMy4wODUxIDIzLjg5NjlDMjMuMzc3MyAyNC4zNzUgMjMuMDcyNyAyNSAyMi41IDI1SDE0Ljg3MzNDMTQuNjA5MyAyNSAxNC4zNjU0IDI1LjExNjYgMTQuMjMzMyAyNS4zMzg1TDEyLjEwMDEgMjguOTI0M0MxMS44MTU3IDI5LjQwMjMgMTIuMTcxMyAzMCAxMi43NCAzMEgzMS4yNTk5QzMxLjgyODkgMzAgMzIuMTg0MyAyOS40MDIzIDMxLjg5OTkgMjguOTI0M0wyMi42Mzk5IDEzLjM1ODZDMjIuMzU1NiAxMi44ODA1IDIxLjY0NDUgMTIuODgwNSAyMS4zNjAxIDEzLjM1ODZaIiBmaWxsPSIjRUEzNTQ2Ii8+Cjwvc3ZnPgo='

export function FooterSection({ useContextFunction }: FooterSectionProps) {
  const currentYear = new Date().getFullYear()
  const { api } = useApiContext()
  const footerRef = useRef<HTMLElement | null>(null)
  const [showStaffDealingModal, setShowStaffDealingModal] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<{
    status: string
    message: string
    loginUrl?: string
  } | null>(null)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isStaffAccessOpen =
    searchParams.has(STAFF_ACCESS_QUERY_KEY) ||
    searchParams.get('modal') === 'staff-access' ||
    searchParams.get('staff') === '1'

  const buildUrlWithStaffAccessState = (open: boolean) => {
    const nextSearchParams = new URLSearchParams(searchParams.toString())
    nextSearchParams.delete(STAFF_ACCESS_QUERY_KEY)
    nextSearchParams.delete('modal')
    nextSearchParams.delete('staff')

    const baseQuery = nextSearchParams.toString()
    const nextQuery = open
      ? baseQuery
        ? `${baseQuery}&${STAFF_ACCESS_QUERY_KEY}`
        : STAFF_ACCESS_QUERY_KEY
      : baseQuery

    return nextQuery ? `${pathname}?${nextQuery}` : pathname
  }

  const scrollFooterIntoView = () => {
    if (typeof window === 'undefined') return

    const footerElement = footerRef.current
    if (!footerElement) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'auto',
      })
      return
    }

    const footerTop = footerElement.getBoundingClientRect().top + window.scrollY
    const targetTop = Math.max(
      0,
      footerTop + footerElement.offsetHeight - window.innerHeight
    )

    window.scrollTo({ top: targetTop, behavior: 'auto' })
  }

  useEffect(() => {
    if (!isStaffAccessOpen) return

    scrollFooterIntoView()

    const animationFrame = window.requestAnimationFrame(() => {
      setShowStaffDealingModal(true)
    })

    return () => {
      window.cancelAnimationFrame(animationFrame)
    }
  }, [isStaffAccessOpen, scrollFooterIntoView])

  const { config, loading, error } =
    useContextFunction() as InvestorRelationsConfigContextType
  const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
    {}) as IRThemeConfig
  const primaryColor = themeConfig.primaryColor
  const fontFamily = themeConfig.fontStyle

  const companyConfig = ((config?.companyConfig as IRCompanyConfig) ||
    {}) as IRCompanyConfig
  const companyName = companyConfig.companyName
  const irContactName = companyConfig.irContactName
  const irContactEmail = companyConfig.irContactEmail
  const socialLinks = [
    {
      label: 'LinkedIn',
      url: companyConfig.companyLinkedinUrl,
      icon: FaLinkedin,
    },
    {
      label: 'YouTube',
      url: companyConfig.companyYoutubeUrl,
      icon: FaYoutube,
    },
    {
      label: 'X',
      url: companyConfig.companyXUrl,
      icon: FaXTwitter,
    },
    {
      label: 'Facebook',
      url: companyConfig.companyFacebookUrl,
      icon: FaFacebook,
    },
    {
      label: 'Instagram',
      url: companyConfig.companyInstagramUrl,
      icon: FaInstagram,
    },
  ].filter((link) => link.url?.trim())

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setResult(null)

    try {
      const response = await api.post('/public/ir/staff/check-email', {
        email: email.trim(),
      })
      setResult(response.data)
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ detail?: string }>
      setResult({
        status: 'error',
        message:
          axiosError.response?.data?.detail ||
          'An error occurred. Please try again later.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseModal = () => {
    setShowStaffDealingModal(false)
    setEmail('')
    setResult(null)
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', buildUrlWithStaffAccessState(false))
    }
  }

  const handleOpenModal = () => {
    setShowStaffDealingModal(true)
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', buildUrlWithStaffAccessState(true))
    }
  }

  return (
    <Box
      as="footer"
      ref={footerRef}
      bg={primaryColor}
      color="white"
      minH="calc(50vh - 25px)"
      display="flex"
      alignItems="center"
    >
      <Container maxW="6xl">
        <Stack gap={{ base: 4, md: 6 }} align="center" textAlign="center">
          <Heading
            as="h2"
            size={{ base: 'md', md: 'lg' }}
            fontWeight="semibold"
            fontFamily={fontFamily}
          >
            Investor Relations
          </Heading>

          <Stack gap={1} align="center">
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight="medium"
              textAlign="center"
              fontFamily={fontFamily}
            >
              {companyName}
            </Text>
            <Text
              fontSize="sm"
              opacity={0.85}
              textAlign="center"
              fontFamily={fontFamily}
            >
              {irContactName}
            </Text>

            <HStack gap={2} fontSize="sm" opacity={0.9} textAlign="center">
              <Icon as={FiMail} boxSize={4} />
              <Link
                href={`mailto:${irContactEmail}`}
                fontFamily={fontFamily}
                _hover={{ textDecoration: 'underline' }}
                _focus={{ outline: 'none' }}
              >
                {irContactEmail}
              </Link>
            </HStack>
            {socialLinks.length > 0 && (
              <HStack gap={3} fontSize="sm" opacity={0.9} textAlign="center">
                {socialLinks.map((socialLink) => (
                  <Link
                    key={socialLink.label}
                    href={socialLink.url}
                    target="_blank"
                    aria-label={`Follow us on ${socialLink.label}`}
                    _hover={{ opacity: 0.75 }}
                    _focus={{ outline: 'none' }}
                  >
                    <Icon as={socialLink.icon} boxSize={4} />
                  </Link>
                ))}
              </HStack>
            )}

            {/* Staff Link */}
            <Link
              as="button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault()
                handleOpenModal()
              }}
              fontSize="xs"
              color="inherit"
              opacity={0.5}
              _hover={{ opacity: 0.7, textDecoration: 'underline' }}
              _focus={{ outline: 'none' }}
              fontFamily={fontFamily}
              cursor="pointer"
              style={{ background: 'none', border: 'none', padding: 0 }}
            >
              Staff
            </Link>
          </Stack>

          <Stack gap={1} align="center" fontSize="xs" opacity={0.9}>
            <HStack gap={1.5}>
              <Text fontFamily={fontFamily}>Powered by</Text>
              <Link
                href="https://www.assiduous.tech/"
                target="_blank"
                aria-label="Assiduous"
                _hover={{ opacity: 0.8 }}
              >
                <Image
                  src={ASSIDUOUS_ICON_DATA_URL}
                  alt="Assiduous"
                  h="16px"
                  w="16px"
                  display="block"
                />
              </Link>
            </HStack>
            <Text fontFamily={fontFamily}>
              © {currentYear} {companyName}. All rights reserved.
            </Text>
          </Stack>
        </Stack>
      </Container>

      {/* Staff Dealing Modal */}
      <Dialog.Root
        open={showStaffDealingModal}
        onOpenChange={(e) => !e.open && handleCloseModal()}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Staff Access</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    Enter your email address to check if you have a staff
                    account or to resend your activation email.
                  </Text>

                  {result && (
                    <Box
                      p={4}
                      borderRadius="md"
                      bg={
                        result.status === 'error'
                          ? 'red.50'
                          : result.status === 'already_activated'
                            ? 'green.50'
                            : result.status === 'pending_activation'
                              ? 'blue.50'
                              : 'gray.50'
                      }
                      border="1px solid"
                      borderColor={
                        result.status === 'error'
                          ? 'red.200'
                          : result.status === 'already_activated'
                            ? 'green.200'
                            : result.status === 'pending_activation'
                              ? 'blue.200'
                              : 'gray.200'
                      }
                    >
                      <Text
                        fontSize="sm"
                        color={
                          result.status === 'error'
                            ? 'red.700'
                            : result.status === 'already_activated'
                              ? 'green.700'
                              : result.status === 'pending_activation'
                                ? 'blue.700'
                                : 'gray.700'
                        }
                      >
                        {result.message}
                      </Text>
                      {result.status === 'already_activated' && (
                        <Link href="/login">
                          <Button mt={3} as="span">
                            Go to Staff Login
                          </Button>
                        </Link>
                      )}
                    </Box>
                  )}

                  <form onSubmit={handleCheckEmail}>
                    <Field.Root>
                      <Field.Label>Email Address</Field.Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@company.com"
                        required
                        disabled={isSubmitting}
                      />
                    </Field.Root>
                    <Button
                      type="submit"
                      mt={4}
                      colorPalette="blue"
                      width="full"
                      loading={isSubmitting}
                    >
                      {isSubmitting ? 'Checking...' : 'Check Email'}
                    </Button>
                  </form>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button onClick={handleCloseModal} variant="ghost">
                  Close
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
